/**
 * Patient-panel parser/type re-exports and relay-signal helpers.
 *
 * Re-exports the clinical parsers and types from clinicalDataParser for
 * consumers that import them from here, and exposes stripRelay for cleaning
 * relay markers out of agent output before display.
 */
import { parseMedReconJSON } from '../utils/clinicalDataParser'
import type {
  PanelSummary,
  PanelOrders,
  VitalSign,
  LabGroup,
  ImagingFinding,
  MedicationCategories,
  PanelResponse,
} from '../utils/clinicalDataParser'

// Re-export parsers for consumers that previously imported from usePatientPanel
export { parseMedReconJSON }

// Re-export types
export type {
  PanelSummary,
  PanelOrders,
  VitalSign,
  LabGroup,
  ImagingFinding,
  MedicationCategories,
  PanelResponse,
}

// ────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────

export type PanelTab = 'summary' | 'results' | 'orders' | 'medications' | 'ipass'

// The med-recon vocabulary lives in patient-panel/types.ts, the panel's own
// type module. Re-exported (not restated) so a status the backend can write
// cannot be missing from one copy of the union -- this one silently lacked
// 'reconciling' until #563.
import type { MedsStatus } from '../components/patient-panel/medsStatus'
export type { MedsStatus }

export interface UsePatientPanelReturn {
  summary: PanelSummary | null
  orders: PanelOrders | null
  vitals: VitalSign[] | null
  labs: LabGroup[] | null
  imaging: ImagingFinding[] | null
  medications: MedicationCategories | null
  medsStatus: MedsStatus
  setMedsStatus: (status: MedsStatus) => void
  ipassHtml: string | null
  activeOrderTab: PanelTab
  setActiveOrderTab: (tab: PanelTab) => void
  isLoading: boolean
  setIsLoading: (loading: boolean) => void
  storageError: boolean
  lastRefreshed: string | null
  processResponse: (output: string) => PanelResponse
  scheduleRefresh: (refreshFn: () => void) => void
  clearPanel: () => void
  updateIpassHtml: (html: string | null) => void
}

// ────────────────────────────────────────────────────────
// Relay signal utilities
// ────────────────────────────────────────────────────────

/**
 * Strip relay signal from display text.
 */
export const stripRelay = (output: string | null | undefined): string | null | undefined => {
  if (!output) return output
  return output.replace(/<!--\s*RELAY:[\s\S]*?-->/g, '').trim()
}
